import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Phone, X, UserPlus, MapPin, Shield, Check, MessageCircle } from "lucide-react";
import { api } from "../api";
import { timeAgo } from "../lib/util";

const RELATIONS = ["Mother", "Father", "Spouse", "Sibling", "Child", "Friend", "Neighbour", "Guardian"];

// Aarya WhatsApp sender number - contacts must open a chat window with this
// number before they can receive template notifications (Meta compliance for
// businesses that are not yet business-verified). See /app/docs/11-WHATSAPP-FUTURE.md
const AARYA_WA_NUMBER = "919270295963";

// Compose a WhatsApp intent URL that the current user shares with a trusted
// contact. When the contact taps the embedded wa.me link, their WhatsApp opens
// pre-filled with a "Hi Aarya" message; hitting Send opens the 24-hour
// customer-service window with the Aarya WABA so all future template messages
// from Aarya can be delivered.
function buildOptInIntent(contact, myName = "") {
  const optInLink = `https://wa.me/${AARYA_WA_NUMBER}?text=${encodeURIComponent(
    `Hi Aarya, please activate my Trust Circle notifications${myName ? ` for ${myName}` : ""}.`
  )}`;
  const message = [
    `Hi ${contact.name || "there"} 🌸`,
    ``,
    `${myName ? myName + " has" : "I have"} added you to ${myName ? "their" : "my"} Trust Circle on Aarya - a small, gentle safety app made in India by IndoBlitz.`,
    ``,
    `To start receiving check-ins and emergency alerts from Aarya on WhatsApp, please tap the link below and send the pre-filled "Hi Aarya" message:`,
    ``,
    optInLink,
    ``,
    `You will only receive messages when ${myName ? "they" : "I"} check-in as safe or trigger a Help alert. That is it. Thank you 🇮🇳`,
  ].join("\n");
  // wa.me opens a chat with the recipient (contact.phone) and pre-fills text.
  const to = contact.phone.replace(/[^\d]/g, "");
  return `https://wa.me/${to}?text=${encodeURIComponent(message)}`;
}

export default function Circle({ onJump }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", relation: "Mother", phone: "", is_child: false, date_of_birth: "", guardian_consent: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [myName, setMyName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: circle }, { data: me }] = await Promise.all([
        api.get("/circle"),
        api.get("/auth/me").catch(() => ({ data: {} })),
      ]);
      setItems(circle);
      setMyName(me?.name || "");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    setErr("");
    if (!form.name.trim() || !form.phone.startsWith("+")) {
      setErr("Name and phone with country code (e.g. +91...) required.");
      return;
    }
    if (form.is_child) {
      if (!form.date_of_birth) { setErr("Please enter the child's date of birth."); return; }
      if (!form.guardian_consent) { setErr("Please tick the guardian consent statement to continue."); return; }
    }
    setBusy(true);
    try {
      await api.post("/circle", form);
      setShowAdd(false);
      setForm({ name: "", relation: "Mother", phone: "", is_child: false, date_of_birth: "", guardian_consent: false });
      await load();
      // Backend automatically sends an SMS opt-in invite from Aarya to the new
      // contact - no manual action needed from the user. The green WhatsApp
      // icon on the contact card is available for optional re-invites.
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not add.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/circle/${id}`);
    await load();
  };

  const statusLabel = (s) => {
    if (!s) return { text: "-", cls: "text-ink-500" };
    const map = {
      safe: { text: `Safe · ${timeAgo(s.at)}`, cls: "text-sage-700" },
      help: { text: `Needs help · ${timeAgo(s.at)}`, cls: "text-brick-500 font-semibold" },
      check_in_okay: { text: `Checked in · ${timeAgo(s.at)}`, cls: "text-terracotta-600" },
      check_in_callme: { text: `Asked to call · ${timeAgo(s.at)}`, cls: "text-terracotta-600" },
      check_in_help: { text: `Needs help · ${timeAgo(s.at)}`, cls: "text-brick-500 font-semibold" },
    };
    return map[s.type] || { text: timeAgo(s.at), cls: "text-ink-500" };
  };

  return (
    <div className="px-6 pt-4" data-testid="circle-screen">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[12px] tracking-[0.16em] uppercase text-ink-500">Your trusted</p>
          <h1 className="font-display text-[34px] leading-[1.05] text-ink-900 mt-1 tracking-tight">Circle of care</h1>
          <p className="text-sm text-ink-500 mt-1">{items.length} {items.length === 1 ? "person" : "people"} who matter most.</p>
        </div>
      </div>

      <div className="mt-6 grid gap-3">
        {/* Invite a loved one CTA */}
        <button
          onClick={() => onJump && onJump("invite")}
          data-testid="circle-invite-parent"
          className="rounded-3xl bg-ink-900 text-white p-5 text-left active:scale-[0.99] transition relative overflow-hidden"
        >
          <div className="absolute inset-0 grain opacity-50"/>
          <div className="relative flex items-center gap-4">
            <div className="h-12 w-12 rounded-2xl bg-terracotta-500 flex items-center justify-center flex-shrink-0">
              <UserPlus size={20}/>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] tracking-[0.18em] uppercase text-white/60 font-semibold">For family · Free</p>
              <p className="font-display text-[18px] leading-tight mt-0.5">Invite a loved one in 60 seconds</p>
              <p className="text-[12px] text-white/70 mt-0.5">Set up Aarya for them, in your name.</p>
            </div>
            <span className="text-2xl text-white/60">→</span>
          </div>
        </button>

        {loading && <div className="text-sm text-ink-500" data-testid="circle-loading">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-3xl border-2 border-dashed border-sand-200 p-6 text-center" data-testid="circle-empty">
            <p className="font-display text-xl text-ink-900">No one here yet.</p>
            <p className="text-sm text-ink-500 mt-1">Add at least one trusted person so we can alert them when it matters.</p>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="rounded-2xl bg-sky-50 border border-sky-200 p-3 flex items-start gap-3" data-testid="wa-optin-hint">
            <MessageCircle size={16} className="text-sky-700 flex-shrink-0 mt-0.5" />
            <p className="text-[12px] text-ink-700 leading-snug">
              Contacts get Aarya alerts on <span className="font-semibold text-sky-700">SMS by default</span> - works on any phone, no setup needed. Tap the <span className="font-semibold text-emerald-700">green WhatsApp icon</span> to also enable free WhatsApp delivery for that contact (they need to tap the invite link once).
            </p>
          </div>
        )}
        {items.map((c) => {
          const s = statusLabel(c.last_status);
          // Meta allows utility templates to opted-in users indefinitely (the 24hr
          // window only applies to free-form session messages, which Aarya doesn't send).
          // Once opted in, stay on WhatsApp - Meta will reject individual sends if the
          // opt-in is actually revoked, and _try_sms_fallback catches that.
          const optedIn = Boolean(c.wa_opted_in_at);
          const channelLabel = optedIn ? "WhatsApp" : "SMS";
          const channelCls = optedIn ? "text-emerald-700 bg-emerald-50" : "text-sky-700 bg-sky-50";
          const inviteStatus = c.invite_status || "active";
          const inviteBadge = {
            pending:  { text: "Invite pending",  cls: "text-amber-700 bg-amber-50 border border-amber-200" },
            active:   { text: "Accepted",         cls: "text-sage-700 bg-sage-50 border border-sage-500/30" },
            declined: { text: "Declined invite",  cls: "text-brick-700 bg-brick-50 border border-brick-500/30" },
            blocked:  { text: "Blocked",          cls: "text-ink-500 bg-sand-100 border border-sand-200" },
          }[inviteStatus] || null;
          return (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-3xl border p-4 flex items-center gap-4 ${inviteStatus === "pending" ? "border-amber-300" : inviteStatus === "declined" ? "border-brick-300 opacity-70" : "border-sand-200"}`}
              data-testid={`contact-${c.id}`}
            >
              <div className="h-14 w-14 rounded-2xl overflow-hidden bg-sand-100 flex-shrink-0">
                {c.avatar_url
                  ? <img src={c.avatar_url} alt={c.name} className="h-full w-full object-cover" />
                  : <div className="h-full w-full flex items-center justify-center font-display text-xl text-ink-900">{(c.name || "?").charAt(0)}</div>
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-ink-900 truncate">{c.name}</p>
                <p className="text-[12px] text-ink-500">{c.relation} · {c.phone}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {inviteBadge && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${inviteBadge.cls}`} data-testid={`invite-status-${c.id}`}>
                      {inviteBadge.text}
                    </span>
                  )}
                  {inviteStatus === "active" && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${channelCls}`} data-testid={`channel-${c.id}`}>
                      Alerts via {channelLabel}
                    </span>
                  )}
                  {inviteStatus === "active" && <p className={`text-[12px] ${s.cls}`}>{s.text}</p>}
                </div>
              </div>
              <a
                href={`tel:${c.phone}`}
                data-testid={`call-${c.id}`}
                className="h-10 w-10 rounded-full bg-sage-50 text-sage-700 flex items-center justify-center"
              ><Phone size={16}/></a>
              <a
                href={buildOptInIntent(c, myName)}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`wa-optin-${c.id}`}
                title={`Send WhatsApp opt-in invite to ${c.name}`}
                className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center"
              ><MessageCircle size={16}/></a>
              <button
                onClick={() => remove(c.id)}
                data-testid={`remove-${c.id}`}
                className="h-10 w-10 rounded-full bg-sand-100 text-ink-500 flex items-center justify-center"
              ><Trash2 size={16}/></button>
            </motion.div>
          );
        })}

        <button
          onClick={() => setShowAdd(true)}
          data-testid="add-contact-button"
          className="rounded-3xl border-2 border-dashed border-sand-200 p-5 flex items-center justify-center gap-2 text-ink-700 hover:bg-white transition"
        >
          <Plus size={18}/> <span className="font-semibold">Add a trusted person</span>
        </button>
      </div>

      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-40 flex items-end sm:items-center justify-center"
            onClick={() => setShowAdd(false)}
          >
            <motion.div
              initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[430px] bg-sand-50 rounded-t-[32px] sm:rounded-3xl p-6 max-h-[92dvh] overflow-y-auto"
              data-testid="add-contact-sheet"
            >
              <div className="flex items-center justify-between">
                <p className="font-display text-2xl text-ink-900">Add to your circle</p>
                <button onClick={() => setShowAdd(false)} data-testid="add-contact-close"
                  className="h-9 w-9 rounded-full bg-white border border-sand-200 flex items-center justify-center"><X size={16}/></button>
              </div>

              <div className="mt-5 space-y-4">
                <div>
                  <label className="text-[12px] tracking-wide uppercase text-ink-500">Name</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Aarti Sharma"
                    data-testid="contact-name"
                    className="mt-1 w-full h-[56px] px-4 rounded-2xl border-2 border-sand-200 bg-white text-base outline-none focus:border-sage-500" />
                </div>
                <div>
                  <label className="text-[12px] tracking-wide uppercase text-ink-500">Relation</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {RELATIONS.map((r) => (
                      <button key={r} onClick={() => setForm({ ...form, relation: r })}
                        data-testid={`relation-${r.toLowerCase()}`}
                        className={`h-10 px-4 rounded-full text-sm border ${form.relation === r ? "bg-ink-900 text-white border-ink-900" : "bg-white text-ink-700 border-sand-200"}`}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[12px] tracking-wide uppercase text-ink-500">Phone (with country code)</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+919876543210" inputMode="tel"
                    data-testid="contact-phone"
                    className="mt-1 w-full h-[56px] px-4 rounded-2xl border-2 border-sand-200 bg-white text-base outline-none focus:border-sage-500" />
                </div>

                <div
                  onClick={() => setForm({ ...form, is_child: !form.is_child, guardian_consent: false })}
                  role="checkbox"
                  aria-checked={form.is_child}
                  data-testid="is-child-toggle"
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white border-2 border-sand-200 active:bg-sand-100 cursor-pointer select-none"
                >
                  <span className={`h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0 border-2 ${form.is_child ? "bg-sage-500 border-sage-500" : "bg-white border-sand-200"}`}>
                    {form.is_child && <Check size={14} className="text-white" strokeWidth={3}/>}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] text-ink-900 font-semibold leading-tight">This person is a child (under 18)</p>
                    <p className="text-[12px] text-ink-500 leading-snug mt-0.5">Enrolling as guardian gives them limited features and unlocks the Safe Adult protection.</p>
                  </div>
                </div>

                {form.is_child && (
                  <div className="space-y-3 pl-1" data-testid="child-fields">
                    <div>
                      <label className="text-[12px] tracking-wide uppercase text-ink-500">Child&apos;s date of birth</label>
                      <input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                        max={new Date().toISOString().slice(0, 10)}
                        data-testid="contact-dob"
                        className="mt-1 w-full h-[52px] px-4 rounded-2xl border-2 border-sand-200 bg-white text-base outline-none focus:border-sage-500" />
                    </div>
                    <div
                      onClick={() => setForm({ ...form, guardian_consent: !form.guardian_consent })}
                      role="checkbox"
                      aria-checked={form.guardian_consent}
                      data-testid="guardian-consent"
                      className="w-full flex items-start gap-3 p-4 rounded-2xl bg-sage-50 border-2 border-sage-500/30 cursor-pointer"
                    >
                      <span className={`h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0 border-2 mt-0.5 ${form.guardian_consent ? "bg-sage-500 border-sage-500" : "bg-white border-sage-500/60"}`}>
                        {form.guardian_consent && <Check size={14} className="text-white" strokeWidth={3}/>}
                      </span>
                      <span className="text-[13px] text-ink-800 leading-snug">
                        I confirm I am the <span className="font-semibold">parent or lawful guardian</span> of {form.name || "this child"}, and I consent on their behalf to Aarya processing the information required to keep them safe.
                      </span>
                    </div>
                    <div className="flex items-start gap-2 px-1">
                      <Shield size={14} className="text-sage-700 flex-shrink-0 mt-0.5"/>
                      <p className="text-[11.5px] text-ink-500 leading-snug">
                        After adding, you can link a <span className="font-semibold text-ink-700">Safe Adult</span> (teacher, aunt, coach outside your household). They receive alerts alongside you and cannot be silently removed.
                      </p>
                    </div>
                  </div>
                )}

                {err && <p className="text-sm text-brick-500" data-testid="contact-error">{err}</p>}
                <button
                  onClick={add}
                  disabled={busy}
                  data-testid="contact-save"
                  className="w-full h-[58px] rounded-full bg-sage-500 hover:bg-sage-600 text-white font-semibold disabled:opacity-50"
                >
                  {busy ? "Adding…" : (form.is_child ? "Add as child (with guardian consent)" : "Add to circle")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
